// biome-ignore lint/performance/noNamespaceImport: <Need for proper Sentry work>
import * as Sentry from "@sentry/react";
import axios, { type AxiosRequestConfig } from "axios";
import { UNAUTHORIZED_STATUS } from "../constants/http-req";
import logger from "./logger";

type RequestConfigWithRetry = AxiosRequestConfig & { _retry?: boolean };

export type SetupUnauthorizedInterceptorOptions = {
  /** Функция обновления токена, возвращает новый access token или null */
  refreshAuthToken: () => Promise<string | null>;
  /** Функция выхода (очистка токенов, store и т.д.) */
  logout: () => void;
  /** Куда редиректить при 401 после неудачного refresh, по умолчанию "/" */
  redirectUrl?: string;
};

/**
 * Вешает на глобальный axios response interceptor: при 401 пробует обновить токен,
 * при успехе повторяет запрос с новым токеном, иначе вызывает logout и редирект.
 * Переиспользуется в finuslugi, sbol и других приложениях с Cognito-авторизацией.
 */
export const setupUnauthorizedInterceptor = (options: SetupUnauthorizedInterceptorOptions): void => {
  const { refreshAuthToken, logout, redirectUrl = "/" } = options;

  const performLogoutAndRedirect = (): void => {
    try {
      logout();
    } catch (logoutError) {
      logger.error("Logout failed:", logoutError);
      Sentry.captureException(logoutError);
    }
    window.location.href = redirectUrl;
  };

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const is401 = error.response?.status === UNAUTHORIZED_STATUS;
      if (!(is401 && error.config)) {
        return Promise.reject(error);
      }

      const originalRequest = error.config as RequestConfigWithRetry;

      if (!originalRequest._retry) {
        originalRequest._retry = true;
        logger.info("Unauthorized (401) - attempting to refresh token");

        try {
          const newToken = await refreshAuthToken();
          if (newToken) {
            if (!originalRequest.headers) {
              originalRequest.headers = {};
            }
            originalRequest.headers.Authorization = newToken;
            return axios(originalRequest);
          }
        } catch (refreshError) {
          logger.error("Token refresh failed:", refreshError);
          Sentry.captureException(refreshError);
        }
      }

      logger.info("Unauthorized (401) - user needs to re-authenticate");
      performLogoutAndRedirect();
      return Promise.reject(error);
    }
  );
};
